/*
 * Copyright 1999-2024 Jagex Ltd.
 */
import { ParsedUrlQuery } from 'querystring';
import { QueryParam } from '@common/types';
import { ExtendedLinkedAccount } from '@stores/linkedAccounts/types';

export enum SocialLinkingProcessState {
  AwaitingCompletion = 'AWAITING_COMPLETION',
  Cancelled = 'CANCELLED',
  Completed = 'COMPLETED',
  Failed = 'FAILED'
}

export interface BaseSocialLinkingProcess {
  id: string;
  state: SocialLinkingProcessState;
  data: Record<any, unknown>;
}
export interface FailedSocialLinkinProcess extends BaseSocialLinkingProcess {
  state: SocialLinkingProcessState.Failed;
  data: {
    errorCode: string;
    errorMessage: string;
  };
}
export type SocialLinkingProcess = BaseSocialLinkingProcess | FailedSocialLinkinProcess;

export enum SocialAuthState {
  AwaitingToken = 'AWAITING_TOKEN',
  AwaitingCode = 'AWAITING_CODE',
  Completed = 'COMPLETED',
  Failed = 'FAILED'
}

export enum SourceType {
  Local = 'LOCAL',
  Remote = 'REMOTE'
}

export enum StateMachineName {
  Amazon = 'amazon',
  Steam = 'steam'
}

export type ValidateUrlsRequestParams = {
  sourceUrl: string;
};

export type SocialLinkRequestParams = {
  sourceType: SourceType;
  sourceUrl: string;
  errorCallbackUrl: string;
};

export type InitSocialLinkResponseBody = {
  id: string;
  stateMachineName: string;
  state: SocialAuthState;
  data: {
    redirectUrl: string;
  };
};

export type SocialLinkRedirect = {
  redirectUrl: string;
};
export type SocialAuthRedirectUrlData = SocialLinkRedirect;
type SocialLinkCallbackSuccessResponse = Omit<SocialLinkRequestParams, 'errorCallbackUrl'> & {
  state: Exclude<SocialAuthState, SocialAuthState.Failed>;
};

type SocialLinkCallbackErrorResponse = Pick<SocialLinkRequestParams, 'errorCallbackUrl'> & {
  state: SocialAuthState.Failed;
};

export type SocialLinkCallbackResponse = SocialLinkCallbackSuccessResponse | SocialLinkCallbackErrorResponse;

export type CompletedSocialAuthData = {
  providerAccountId: string;
  providerAccountInfo: Record<string, any>;
  provider: string;
};

export type FailedSocialFlowData = {
  error: string;
};

export enum SocialFlowState {
  AwaitingCode = 'AWAITING_CODE',
  AwaitingToken = 'AWAITING_TOKEN',
  DataFetched = 'DATA_FETCHED',
  Completed = 'COMPLETED',
  Failed = 'FAILED'
}
export type AmazonSocialAuthCodeResponse = {
  id: string;
  stateMachineName: StateMachineName.Amazon;
  state: SocialFlowState.AwaitingCode;
  data: SocialAuthRedirectUrlData;
};

export type AmazonSocialAuthDataFetchedResponse = {
  id: string;
  stateMachineName: StateMachineName.Amazon;
  state: SocialFlowState.DataFetched;
  data: CompletedSocialAuthData;
};

export type AmazonSocialAuthCompletedResponse = {
  id: string;
  stateMachineName: StateMachineName.Amazon;
  state: SocialFlowState.Completed;
  data: CompletedSocialAuthData;
};

export type AmazonSocialAuthFailedResponse = {
  id: string;
  stateMachineName: StateMachineName.Amazon;
  state: SocialFlowState.Failed;
  data: FailedSocialFlowData;
};

export type AmazonSocialAuthResponse =
  | AmazonSocialAuthCodeResponse
  | AmazonSocialAuthDataFetchedResponse
  | AmazonSocialAuthCompletedResponse
  | AmazonSocialAuthFailedResponse;

export type SteamSocialAuthTokenResponse = {
  id: string;
  stateMachineName: StateMachineName.Steam;
  state: SocialFlowState.AwaitingToken;
  data: SocialAuthRedirectUrlData;
};

export type SteamSocialAuthDataFetchedResponse = {
  id: string;
  stateMachineName: StateMachineName.Steam;
  state: SocialFlowState.DataFetched;
  data: CompletedSocialAuthData;
};

export type SteamSocialAuthCompletedResponse = {
  id: string;
  stateMachineName: StateMachineName.Steam;
  state: SocialFlowState.Completed;
  data: CompletedSocialAuthData;
};

export type SteamSocialAuthFailedResponse = {
  id: string;
  stateMachineName: StateMachineName.Steam;
  state: SocialFlowState.Failed;
  data: FailedSocialFlowData;
};

export type SteamSocialAuthResponse =
  | SteamSocialAuthTokenResponse
  | SteamSocialAuthDataFetchedResponse
  | SteamSocialAuthCompletedResponse
  | SteamSocialAuthFailedResponse;

export type SocialFlowResponse = AmazonSocialAuthResponse | SteamSocialAuthResponse;

export interface SocialAuthInfo extends ParsedUrlQuery {
  state: QueryParam;
  code: QueryParam;
}

export type CheckSocialVerifierRequestParams = SocialAuthInfo;
export type CheckSocialVerifierRequestData = CheckSocialVerifierRequestParams;

export type DeleteSocialLinkRequestParams = Pick<ExtendedLinkedAccount, 'id'>;

export enum ProceedSocialLinkingVariant {
  Complete = 'COMPLETE',
  Cancel = 'CANCEL'
}

export type CompleteSocialLinkingData = {
  socialFlowId: string;
};
export type CancelSocialLinkingData = CompleteSocialLinkingData;
